import { Controller } from "@hotwired/stimulus"

// Bulle de catégorie sur une journée : n'affiche plus que ce type, pour cette journée-là.
// Les autres journées et les pastilles du haut ne bougent pas. Un second clic sur la bulle
// active retire le filtre.
//
// Les lignes déjà rendues sont celles du mélange, vingt par vingt. La catégorie cliquée peut
// n'y être presque pas (neuf sinistres noyés dans six cents souscriptions) : on va donc
// chercher cette catégorie pour la journée, et on suspend la suite mélangée pour ne pas la
// dérouler en dessous.
export default class extends Controller {
  static values = { path: String }

  connect() {
    this.filters = new Map()
    this.onFrameLoad = () => this.apply()
    this.element.addEventListener("turbo:frame-load", this.onFrameLoad)
  }

  disconnect() {
    this.element.removeEventListener("turbo:frame-load", this.onFrameLoad)
  }

  toggle({ params: { day, kind } }) {
    const key = String(day)
    const next = this.filters.get(key) === kind ? null : kind

    if (next) {
      this.filters.set(key, next)
      this.reveal(key)
    } else {
      this.filters.delete(key)
    }

    this.apply()
    next ? this.holdMixedTail(key) : this.releaseMixedTail(key)
  }

  apply() {
    const days = [...this.filters.keys()]
    if (days.length) this.element.dataset.filteredDays = days.join(" ")
    else delete this.element.dataset.filteredDays

    this.element.querySelectorAll(".recent-activity__day-count").forEach((button) => {
      const day = button.dataset.recentActivityDayFilterDayParam
      const kind = button.dataset.recentActivityDayFilterKindParam
      button.setAttribute("aria-pressed", this.filters.get(day) === kind ? "true" : "false")
    })

    this.element.querySelectorAll("[data-group]").forEach((body) => {
      if (body.closest(".recent-activity__kind-feed")) return

      const kind = this.filters.get(body.dataset.group)
      if (kind) body.dataset.dayFilter = kind
      else delete body.dataset.dayFilter
    })

    this.filters.forEach((kind, day) => this.ensureKindFeed(day, kind))
  }

  // Replier masque le résultat : on rouvre la journée qu'on vient de filtrer.
  reveal(day) {
    const trigger = this.element.querySelector(
      `button[data-collapsible-groups-group-param="${CSS.escape(day)}"]`
    )
    if (trigger?.getAttribute("aria-expanded") === "false") trigger.click()
  }

  ensureKindFeed(day, kind) {
    const body = [...this.element.querySelectorAll(`[data-group="${CSS.escape(day)}"]`)].find(
      (node) => !node.closest(".recent-activity__kind-feed")
    )
    if (!body || body.querySelector(`:scope > .recent-activity__kind-feed[data-kind="${kind}"]`)) return

    const frame = document.createElement("turbo-frame")
    frame.id = `dashboard_recent_activity_day_${day}_${kind}`
    frame.className = "recent-activity__kind-feed"
    frame.dataset.kind = kind
    frame.setAttribute("loading", "eager")
    frame.setAttribute("src", this.kindFeedUrl(day, kind))

    const placeholder = this.element.querySelector(".recent-activity__loading")
    if (placeholder) frame.appendChild(placeholder.cloneNode(true))

    body.appendChild(frame)
  }

  kindFeedUrl(day, kind) {
    const url = new URL(this.pathValue, window.location.origin)
    url.searchParams.set("since_day", day)
    url.searchParams.append("kinds[]", kind)
    url.searchParams.set("entries_only", "1")
    return `${url.pathname}${url.search}`
  }

  // La suite mélangée de cette journée ne doit pas défiler pendant le filtre : on la range,
  // et le jour d'avant reste accessible par la frame qui saute la journée.
  holdMixedTail(day) {
    const groups = this.groups
    if (!groups?.hasBeforeParamValue) return

    this.mixedFrames(day).forEach((frame) => {
      frame.classList.add("is-filtered-aside")
      groups.suspendContinuation(frame, day)
      groups.ensureSkipFrame(frame, day)
    })
  }

  releaseMixedTail(day) {
    this.mixedFrames(day).forEach((frame) => frame.classList.remove("is-filtered-aside"))
    this.groups?.resumeContinuation(day)
  }

  mixedFrames(day) {
    return [...this.element.querySelectorAll("turbo-frame[src]")].filter((frame) => {
      if (frame.dataset.skipDay || frame.classList.contains("recent-activity__kind-feed")) return false
      if (frame.closest(".recent-activity__kind-feed")) return false
      return this.groups?.dayParam(frame, "previous_day") === day
    })
  }

  get groups() {
    return this.application.getControllerForElementAndIdentifier(this.element, "collapsible-groups")
  }
}
